import React, { useState, useEffect, useRef } from 'react';
import './App.css';
import ChatInput from './components/ChatInput';
import ChatMessage from './components/ChatMessage';
import Disclaimer from './components/Disclaimer';
import MainInterface from './components/MainInterface';
import ExitConfirmation from './components/ExitConfirmation';
import CrisisPopup from './components/CrisisPopup';
import AdminLogin from './components/AdminLogin';
import AdminDashboard from './components/AdminDashboard';
import Logo from './components/Logo';
import { LogoProvider } from './contexts/LogoContext';
import axios from 'axios';

// Simple router function to handle admin routes
const useRouter = () => {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => {
      setCurrentPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const navigate = (path) => {
    window.history.pushState({}, '', path);
    setCurrentPath(path);
  };

  return { currentPath, navigate };
};

// Generate enhanced browser fingerprint for better session management and privacy
const generateBrowserFingerprint = () => {
  try {
    const fingerprint = {
      userAgent: navigator.userAgent,
      language: navigator.language,
      platform: navigator.platform,
      screenResolution: `${screen.width}x${screen.height}`,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      tabId: Math.random().toString(36).substr(2, 9),
      created: Date.now()
    };

    const fingerprintString = JSON.stringify(fingerprint);
    let hash = 0;
    for (let i = 0; i < fingerprintString.length; i++) {
      const char = fingerprintString.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }

    const finalFingerprint = `${Math.abs(hash).toString(36)}-${Date.now() % 1000000}`;
    sessionStorage.setItem('browserFingerprint', finalFingerprint);
    return finalFingerprint;

  } catch (error) {
    const fallback = `fallback-${Date.now()}-${Math.random().toString(36).substr(2, 8)}`;
    sessionStorage.setItem('browserFingerprint', fallback);
    return fallback;
  }
};

// Auto-detect environment and API URL
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  const hostname = window.location.hostname;
  
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8000';
  }
  
  // For production, use the same hostname without port (nginx proxy)
  return `${window.location.protocol}//${hostname}`;
};

// Test API URL connectivity
const testApiUrl = async (url) => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000);
    
    const response = await fetch(`${url}/health`, { 
      signal: controller.signal,
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    });
    
    clearTimeout(timeoutId);
    return response.ok;
  } catch (error) {
    return false;
  }
};

const getWorkingApiUrl = async () => {
  const primaryUrl = getApiUrl();
  // For production, only try the nginx proxy URL
  const fallbackUrls = [];
  
  // Only add localhost fallbacks if we're actually on localhost
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    fallbackUrls.push(
      'http://localhost:8000',
      'http://127.0.0.1:8000'
    );
  }
  
  if (await testApiUrl(primaryUrl)) {
    return primaryUrl;
  }
  
  for (const url of fallbackUrls) {
    if (url !== primaryUrl && await testApiUrl(url)) {
      return url;
    }
  }
  
  return primaryUrl;
};

const ThinkingAnimation = () => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '1rem',
    color: 'var(--color-text-secondary)',
    fontStyle: 'italic'
  }}>
    <div style={{
      display: 'flex',
      gap: '0.2rem'
    }}>
      {[0, 1, 2].map(i => (
        <div
          key={i}
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: 'var(--color-primary)',
            animation: `pulse 1.4s infinite ${i * 0.2}s`
          }}
        />
      ))}
    </div>
    S.U.E. is thinking...
  </div>
);

function App() {
  const { currentPath } = useRouter();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(true);
  const [showMainInterface, setShowMainInterface] = useState(false);
  const [showExitConfirmation, setShowExitConfirmation] = useState(false);
  const [showCrisisPopup, setShowCrisisPopup] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const [sessionId, setSessionId] = useState('');
  const [isMobile, setIsMobile] = useState(false);
  const [adminToken, setAdminToken] = useState(null);
  const messagesEndRef = useRef(null);
  const [ageToggle, setAgeToggle] = useState(false);
  const [voiceSelection, setVoiceSelection] = useState('base');

  useEffect(() => {
    const initializeApp = async () => {
      const workingApiUrl = await getWorkingApiUrl();
      setApiUrl(workingApiUrl);
      axios.defaults.baseURL = workingApiUrl;
      
      const storedFingerprint = sessionStorage.getItem('browserFingerprint');
      const fingerprint = storedFingerprint || generateBrowserFingerprint();
      setSessionId(fingerprint);
      
      const token = localStorage.getItem('adminToken');
      if (token) {
        // Verify token is still valid
        try {
          const response = await axios.get('/api/admin/stats', {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (response.status === 200) {
            setAdminToken(token);
          } else {
            // Token invalid, remove it
            localStorage.removeItem('adminToken');
            setAdminToken(null);
          }
        } catch (error) {
          // Token invalid or expired, remove it
          localStorage.removeItem('adminToken');
          setAdminToken(null);
        }
      }
    };
    
    initializeApp();
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDisclaimerAccept = () => {
    setShowDisclaimer(false);
    setShowMainInterface(true);
  };

  const handleQuestionSelect = (question) => {
    setShowMainInterface(false);
    handleSend(question);
  };

  const handleExitSession = () => {
    setShowExitConfirmation(true);
  };

  const handleExitConfirm = async () => {
    try {
      if (apiUrl && sessionId) {
        await axios.post(`/api/end-session/${sessionId}`);
      }
    } catch (error) {
      console.error('Error ending session:', error);
    } finally {
      setMessages([]);
      setShowExitConfirmation(false);
      setShowDisclaimer(true);
      setShowMainInterface(false);
      
      const newFingerprint = generateBrowserFingerprint();
      setSessionId(newFingerprint);
    }
  };

  const handleExitCancel = () => {
    setShowExitConfirmation(false);
  };

  const handleSend = async (msg) => {
    if (!msg.trim() || loading) return;

    const userMessage = { role: 'user', text: msg };
    setMessages(prev => [...prev, userMessage]);
    setLoading(true);

    // Auto-scroll to bottom after user message
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);

    try {
      const response = await axios.post('/api/chat', {
        message: msg,
        session_id: sessionId,
        // context: messages.slice(-10).filter(m => m.role !== 'system-notice'),
	      age_toggle: ageToggle,
        voice_selection: voiceSelection,
        clear_history: justToggledRef.current
      });

      justToggledRef.current = false;

      // Update sessionId with the actual session_id returned by backend
      if (response.data.session_id && response.data.session_id !== sessionId) {
        console.log(`Updating sessionId from ${sessionId} to ${response.data.session_id}`);
        setSessionId(response.data.session_id);
      }

      const botMessage = { role: 'assistant', text: response.data.response };
      setMessages(prev => [...prev, botMessage]);

      // Auto-scroll to bottom after bot response
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);

      if (response.data.crisis_detected) {
        setShowCrisisPopup(true);
      }
    } catch (error) {
      console.error('Chat error:', error);
      const errorMessage = { 
        role: 'assistant', 
        text: 'Sorry, I encountered an error. Please try again.' 
      };
      setMessages(prev => [...prev, errorMessage]);
      
      // Auto-scroll to bottom after error message
      setTimeout(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleCrisisPopupClose = () => {
    setShowCrisisPopup(false);
  };

  const handleSafelyClose = () => {
    setShowCrisisPopup(false);
  };

  const handleCrisisResourcesSave = async () => {
    try {
      await axios.post('/api/save-crisis-resources', { session_id: sessionId });
      setShowCrisisPopup(false);
    } catch (error) {
      console.error('Error saving crisis resources:', error);
      setShowCrisisPopup(false);
    }
  };

  const handleDownload = async () => {
    try {
      const response = await axios.get(`/download-conversation/${sessionId}`);
      
      if (response.data.error) {
        alert(response.data.error);
        return;
      }

      // Convert base64 to blob
      const base64Data = response.data.pdf_data;
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: 'application/pdf' });
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.data.filename || `chat-conversation-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const handleClearHistory = async () => {
    if (window.confirm('Are you sure you want to clear the chat history?')) {
      try {
        await axios.post('/api/clear-history', { session_id: sessionId });
        setMessages([]);
      } catch (error) {
        console.error('Error clearing history:', error);
      }
    }
  };

  const handleAdminLogin = (token) => {
    setAdminToken(token);
    localStorage.setItem('adminToken', token);
  };

  const handleAdminLogout = () => {
    setAdminToken(null);
    localStorage.removeItem('adminToken');
  };
  const justToggledRef = useRef(false);

  const handleAgeToggle = async () => {
    const newValue = !ageToggle;
    setAgeToggle(newValue);
    justToggledRef.current = true;  // flag the next send

    const toggleMessage = {
      role: 'system-notice',
      text: newValue ? 'Primary Mode is on!' : 'Secondary Mode is on!'
    };
    setMessages(prev => [...prev, toggleMessage]);

    try {
      await axios.post('/api/set-age-toggle', {
        session_id: sessionId,
        enabled: newValue
      });
    } catch (error) {
      console.error('Age toggle error:', error);
    }
    // Tell the chat the age has been toggled for the pdf feature
    await axios.post('/api/log-toggle-event', {
      session_id: sessionId,
      enabled: newValue
    });
  };

  const handleVoiceSelection = (e) => {
    setVoiceSelection(e.target.value);
  };

  // Route handling
  if (currentPath === '/admin' || currentPath === '/admin/') {
    if (!adminToken) {
      return (
        <LogoProvider>
          <AdminLogin onLogin={handleAdminLogin} />
        </LogoProvider>
      );
    } else {
      return (
        <LogoProvider>
          <AdminDashboard onLogout={handleAdminLogout} />
        </LogoProvider>
      );
    }
  }

  // Show disclaimer first
  if (showDisclaimer) {
    return (
      <LogoProvider>
        <Disclaimer onAccept={handleDisclaimerAccept} />
      </LogoProvider>
    );
  }

  // Show main interface
  if (showMainInterface) {
    return (
      <LogoProvider>
        <MainInterface
          onQuestionSelect={handleQuestionSelect}
          onExitSession={handleExitSession}
          ageToggle={ageToggle}
          onAgeToggle={handleAgeToggle}
          voice={voiceSelection}
          onVoiceChange={handleVoiceSelection}
        />
      </LogoProvider>
    );
  }

  // Main chat interface
  return (
    <LogoProvider>
      <div style={{ 
        background: 'var(--color-bg)',
        minHeight: '100vh', 
        display: 'flex', 
        flexDirection: 'column',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        <style>
          {`
            @keyframes pulse {
              0%, 80%, 100% {
                transform: scale(0.8);
                opacity: 0.5;
              }
              40% {
                transform: scale(1);
                opacity: 1;
              }
            }
          `}
        </style>

        {/* Modern Header */}
        <header style={{
          background: 'linear-gradient(135deg, #bada55 0%, #f4ffa9  50%, #bada55 100%)',
          color: '#133153',
          padding: '1.5rem 2rem',
          textAlign: 'center',
          position: 'relative',
          boxShadow: '0 2px 10px rgba(66, 106, 90, 0.2)'
        }}>
          <button 
            onClick={handleExitSession}
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              color: '#133153',
              border: 'none',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              fontSize: '12px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              position: 'absolute',
              left: '1.5rem'
            }}
          >
            ← Exit
          </button>
          
          <h1 style={{
            fontSize: '1.75rem',
            fontWeight: '700',
            margin: '0',
            textShadow: '0 1px 2px rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '0.5rem',
            color: '#133153'
          }}>
            <Logo size="2rem" />
            S.U.E.
          </h1>
          
          {/* Desktop Buttons */}
          {!isMobile && (
            <div style={{
              position: 'absolute',
              right: '1.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '0.5rem'
            }}>
              {/* Add new buttons here for desktop */}
              {/* Drop down selection for the voice options */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#153133',
                fontSize: '12px'
              }}>
                <label>S.U.E. Style: </label>
                <select
                  value={voiceSelection}
                  onChange={handleVoiceSelection}
                  className="voice-select"
                >
                  <option value="base">Base S.U.E. (default, educator)</option>
                  <option value="direct">Direct S.U.E. (clear and efficient)</option>
                  <option value="warm">Peer S.U.E. (relatable and reassuring)</option>
                </select>
              </div>
              {/* Toggle selection for the age mode */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#153133',
                fontSize: '12px'
              }}>
                <span>{ageToggle ? 'Primary Mode' : 'Primary Mode'}</span>
                <div
                  onClick={handleAgeToggle}
                  style={{
                    width: '44px',
                    height: '24px',
                    borderRadius: '12px',
                    background: ageToggle ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                    cursor: 'pointer',
                    position: 'relative',
                    transition: 'background 0.2s ease',
                    flexShrink: 0
                  }}
                >
                  <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: ageToggle ? '23px' : '3px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: ageToggle ? 'var(--color-dark-green)' : 'white',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
              </div>
              <button 
                onClick={handleDownload}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: '#153133',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📄 PDF
              </button>
              <button 
                onClick={handleClearHistory}
                style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  color: '#153133',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '0.5rem 1rem',
                  fontSize: '12px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                🗑️ Clear
              </button>
            </div>
          )}

          {/* Mobile Buttons */}
          {isMobile && (
            <div style={{
              position: 'absolute',
              right: '1.5rem',
              top: '50%',
              transform: 'translateY(-50%)',
              display: 'flex',
              gap: '0.5rem',
              zIndex: 1000
            }}>
              {/* Add new buttons here */}
              {/* Drop down selection for the voice options */}
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                color: '#153133',
                fontSize: '12px'
              }}>
                <label>SUE Style: </label>
                <select
                  value={voiceSelection}
                  onChange={handleVoiceSelection}
                  className="voice-select"
                >
                  <option value="base">Base S.U.E. (default, educator)</option>
                  <option value="direct">Direct S.U.E. (clear and efficient)</option>
                  <option value="warm">Peer S.U.E. (relatable and reassuring)</option>
                </select>
              </div>
            {/* Toggle selection for the age level */}
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '2px',
                cursor: 'pointer'
              }}
                onClick={handleAgeToggle}
              >
                <div style={{
                  width: '44px',
                  height: '24px',
                  borderRadius: '12px',
                  background: ageToggle ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.3)',
                  position: 'relative',
                  transition: 'background 0.2s ease',
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '3px',
                    left: ageToggle ? '23px' : '3px',
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: ageToggle ? 'var(--color-dark-green)' : 'white',
                    transition: 'left 0.2s ease',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
                  }} />
                </div>
                <span style={{ color: '#153133', fontSize: '10px' }}>
                  {ageToggle ? 'Primary Mode' : 'Primary Mode'}
                </span>
              </div>
              <button 
                onClick={handleDownload}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#153133',
                  border: '2px solid rgba(255,255,255,0.8)',
                  borderRadius: '8px',
                  padding: '0.6rem',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
                title="Download PDF"
              >
                📄
              </button>
              <button 
                onClick={handleClearHistory}
                style={{
                  background: 'rgba(255, 255, 255, 0.9)',
                  color: '#153133',
                  border: '2px solid rgba(255,255,255,0.8)',
                  borderRadius: '8px',
                  padding: '0.6rem',
                  fontSize: '16px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  minWidth: '44px',
                  minHeight: '44px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.3)'
                }}
                title="Clear History"
              >
                🗑️
              </button>

            </div>
          )}
        </header>
        <div className='subtitle-sue-info'>
          <p>
              I am in <strong>{ageToggle ? 'Primary' : 'Secondary'}</strong> Mode and <strong>{voiceSelection === 'base' ? 'Base S.U.E.' : voiceSelection === 'direct' ? 'Direct S.U.E.' : 'Peer S.U.E.'}</strong> style!
          </p>
        </div>

        {/* Chat Container */}
        <div style={{ 
          flex: 1, 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          // padding: 'min(5vh, 20px) min(2rem, 16px)',     I removed this line because the div above that has the info about SUE style is creating the padding now. 
          minHeight: 0 
        }}>
          <main style={{ 
            maxWidth: '1000px', 
            width: '100%', 
            height: 'min(85vh, calc(100vh - 120px))',
            maxHeight: '700px',
            background: 'white',
            borderRadius: 'min(20px, 4vw)',
            boxShadow: '0 10px 40px rgba(255, 107, 107, 0.1)',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid rgba(255, 107, 107, 0.1)'
          }}>
            
            {/* Chat Messages Area */}
            <div style={{ 
              flex: 1, 
              overflowY: 'auto', 
              padding: 'min(2rem, 16px)', 
              display: 'flex', 
              flexDirection: 'column',
              gap: 'min(1rem, 12px)',
              scrollBehavior: 'smooth'
            }}>
              {/* In-Tool Lock Box Message */}
              <div style={{
                background: 'linear-gradient(135deg, #bada55 0%, #f4ffa9 100%)',
                color: 'white',
                padding: '1rem',
                borderRadius: '15px',
                marginBottom: '1rem',
                boxShadow: '0 4px 15px rgba(66, 106, 90, 0.2)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem', color: '#133153' }}>
                  <span style={{ fontSize: '24px', marginRight: '0.5rem'}}>🔐</span>
                  <strong>Private & Confidential Chat</strong>
                </div>
                <div style={{ fontSize: '14px', lineHeight: '1.5',color: '#133153' }}>
                  <p style={{ margin: '0 0 0.5rem 0' }}>💬 I'm here to answer your questions about sexual health, consent, relationships, and more.</p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>📚 My answers come from S.U.E.: Sexual Health Education's trusted Canadian curriculum and public, evidence-based resources.</p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>🐣 I'm now equipped to answer questions for grades 4-7: toggle on <strong>primary mode</strong>!</p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>🗣️ I can also answer as an educator (base), directly, or as a peer. Check the options in the top corner!</p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>📊 We collect your questions anonymously to keep the tool accurate, safe, and inclusive.</p>
                  <p style={{ margin: '0 0 0.5rem 0' }}>🔐 This chat is private and confidential, but it's not a health care portal — please don't share personal or identifying health info.</p>
                  <p style={{ margin: '0' }}>👤 A human does not monitor this chat in real time. We review anonymized data to ensure safety and accuracy, but we cannot provide crisis or medical services. This tool is for general education only.</p>
                </div>
              </div>
              
              {messages.map((msg, idx) => {
                if (msg.role === 'system-notice') {
                  return (
                    <div key={idx} style={{
                      textAlign: 'center',
                      fontSize: '13px',
                      color: '#555',
                      backgroundColor: '#f4ffa9',
                      border: '1px solid #bada55',
                      borderRadius: '10px',
                      padding: '0.5rem 1rem',
                      margin: '0.25rem auto',
                      maxWidth: '80%'
                    }}>
                      {msg.text}
                    </div>
                  );
                }
                return <ChatMessage key={idx} role={msg.role} text={msg.text} />;
              })}
              
              {loading && <ThinkingAnimation />}
              
              <div ref={messagesEndRef} />
            </div>
            
            {/* Chat Input */}
            <div style={{ 
              padding: 'min(1.5rem, 16px)', 
              borderTop: '1px solid #f0f0f0',
              background: '#fafafa'
            }}>
              <ChatInput onSend={handleSend} disabled={loading} />
              <div style={{
                textAlign: 'center',
                marginTop: '0.5rem',
                fontSize: '12px',
                color: 'var(--color-text-secondary)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.25rem'
              }}>
                <Logo size="14px" />
                <span>This model is trained on evidence-based curriculum and vetted public resources by TWS.</span>
              </div>
            </div>
          </main>
        </div>

        {/* Crisis Popup */}
        {showCrisisPopup && (
          <CrisisPopup 
            onClose={handleCrisisPopupClose}
            onSafelyClose={handleSafelyClose}
            onSaveResources={handleCrisisResourcesSave}
          />
        )}

        {/* Exit Confirmation Modal */}
        {showExitConfirmation && (
          <ExitConfirmation 
            onConfirm={handleExitConfirm}
            onCancel={handleExitCancel}
          />
        )}
      </div>
    </LogoProvider>
  );
}

export default App; 
